// 市区町村の名前引き。1,892 件を毎入力で線形走査しても十分速いので索引は持たず、
// 「どの一致がユーザーの意図に近いか」の順位付けだけをここに閉じる。
import type { Muni } from "../../schema/muni";

export interface SearchHit {
  muni: Muni;
}

export const SEARCH_LIMIT = 8;

// 小さいほど上位。前方一致 > 部分一致 > 都道府県込みの一致、の順にユーザーの意図に近い。
const RANK_NAME_PREFIX = 0;
const RANK_NAME_PARTIAL = 1;
const RANK_FULL_PARTIAL = 2;
const RANK_NO_MATCH = 3;

/**
 * 全角空白を含む空白をすべて落とし、英字は小文字に寄せる。「東京都 江東区」と「東京都江東区」、
 * 「Tokyo」と「tokyo」を同じ問い合わせとして扱う（日本語は toLowerCase の影響を受けない）。
 */
function normalize(text: string): string {
  return text.replace(/　/g, " ").replace(/\s+/g, "").toLowerCase();
}

function rankOf(muni: Muni, q: string, alias: string): number {
  const name = normalize(muni.name);
  const pref = normalize(muni.pref);
  if (name.startsWith(q)) return RANK_NAME_PREFIX;
  if (name.includes(q)) return RANK_NAME_PARTIAL;
  if (`${pref}${name}`.includes(q) || pref.includes(q)) return RANK_FULL_PARTIAL;
  if (alias !== "" && normalize(alias).includes(q)) return RANK_FULL_PARTIAL;
  return RANK_NO_MATCH;
}

/**
 * alias は名前以外にも当てたい表記を返す関数。英語表示では都道府県のローマ字を渡すので、
 * 「Tokyo」と打っても東京都の市区町村が引ける（市区町村名自体は日本語のまま）。
 */
export function searchMunis(
  munis: Muni[],
  query: string,
  limit = SEARCH_LIMIT,
  alias: (muni: Muni) => string = () => "",
): Muni[] {
  const q = normalize(query);
  if (q === "") return [];

  const scored: { muni: Muni; rank: number }[] = [];
  for (const muni of munis) {
    const rank = rankOf(muni, q, alias(muni));
    if (rank !== RANK_NO_MATCH) scored.push({ muni, rank });
  }

  scored.sort((a, b) => a.rank - b.rank || a.muni.code.localeCompare(b.muni.code));
  return scored.slice(0, limit).map((s) => s.muni);
}
